import React, { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import Banner from '../common/Banner';
import { LoadingSpinner } from '../common';
import { ClaimsService } from '../../services/billing/claims.service';
import { ApiError } from '../../dtos/common/api.dto';
import type {
  BillingConfig as BillingConfigDto, BillingService, ClaimForm, ClaimPortal, ClaimRuleOverride, PayerRule, ServiceCodeRow
} from '../../dtos/billing/claims.dto';
import { isEvvUnitsMatched } from '../../dtos/billing/claims.dto';
import { serviceCodes } from '../../constants/disciplines';

// Phase 20A: active visit services from the discipline registry
const SERVICES = serviceCodes() as BillingService[];
const PORTALS: ClaimPortal[] = ['TEXMEDCONNECT', 'AVAILITY', 'MCO_PORTAL', 'PAPER', 'OTHER'];
const cell = 'w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500';

const emptyRow = (): ServiceCodeRow => ({
  payerKey: 'MEDICAID', service: 'PDN', discipline: 'RN', hcpcs: '', modifiers: [], unitType: 'units',
  minutesPerUnit: 15, ratePerUnit: null, requiresAuth: true, description: '', rateSource: null, rateEffectiveFrom: null,
  evvUnitsMatched: true
});

/** Phase 22 (EVV G6): tooltip on the per-row "Units matched in EVV" toggle */
export const EVV_UNITS_MATCHED_TIP = 'Turn off only for services the HHSC EVV Service Bill Codes table marks as not unit-matched.';

/** One editable row per payer + service claim-form override (flattened from payerRules). */
export interface ClaimFormRow { payerKey: string; service: BillingService | '*'; rule: ClaimRuleOverride }

const DEFAULT_PAYER_RULE: PayerRule = { renderingNpiMode: 'agency', box32Mode: 'blank', box33bBenefitCode: false };
const emptyClaimFormRow = (): ClaimFormRow => ({ payerKey: 'MEDICAID:MCO:', service: 'PDN', rule: { form: 'CMS1500', source: '' } });

export function claimFormRows(payerRules: Record<string, PayerRule>): ClaimFormRow[] {
  return Object.entries(payerRules || {}).flatMap(([payerKey, rule]) =>
    Object.entries(rule.claimRules || {}).map(([service, r]) => ({ payerKey, service: service as BillingService | '*', rule: r as ClaimRuleOverride })));
}

/** Rebuild payerRules from the edited claim-form rows, keeping each payer's other settings. */
export function withClaimFormRows(payerRules: Record<string, PayerRule>, rows: ClaimFormRow[]): Record<string, PayerRule> {
  const out: Record<string, PayerRule> = {};
  for (const [key, rule] of Object.entries(payerRules || {})) {
    const { claimRules: _omit, ...rest } = rule;
    out[key] = rest;
  }
  for (const row of rows) {
    const key = row.payerKey.trim().toUpperCase();
    if (!key) continue;
    const base = out[key] || { ...DEFAULT_PAYER_RULE };
    out[key] = { ...base, claimRules: { ...(base.claimRules || {}), [row.service]: row.rule } };
  }
  return out;
}

/**
 * Billing codes, rates and claim forms (Phase 11 REQ-BIL-07, Phase 17 REQ-CLM-01/05; owner/admin).
 * Each code row maps a payer (MEDICAID, MEDICAID:MCO:<code>, MEDICAID:<program>, COMMERCIAL or
 * COMMERCIAL:<PAYER NAME>) and a service (+ discipline, or * for any) to the HCPCS/CPT code, modifiers,
 * unit size and the contracted rate per unit with its source. A visit with no matching row, or a row
 * with no rate, cannot be billed. Claim-form rows confirm the form per payer and service.
 */
const BillingConfig: React.FC = () => {
  const [config, setConfig] = useState<BillingConfigDto | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    ClaimsService.getBillingConfig().then(setConfig).catch((err) => setError(err instanceof Error ? err.message : 'Failed to load the configuration'));
  }, []);

  if (!config) return error ? <p role="alert" className="p-4 text-sm text-red-700">{error}</p> : <LoadingSpinner className="justify-start p-4" />;

  const setRow = (i: number, patch: Partial<ServiceCodeRow>) => {
    setSaved(false);
    setConfig({ ...config, serviceCodes: config.serviceCodes.map((r, k) => (k === i ? { ...r, ...patch } : r)) });
  };

  const formRows = claimFormRows(config.payerRules);
  const setFormRows = (rows: ClaimFormRow[]) => {
    setSaved(false);
    setConfig({ ...config, payerRules: withClaimFormRows(config.payerRules, rows) });
  };
  const setFormRow = (i: number, patch: Partial<ClaimFormRow>, rulePatch: Partial<ClaimRuleOverride> = {}) =>
    setFormRows(formRows.map((r, k) => (k === i ? { ...r, ...patch, rule: { ...r.rule, ...rulePatch } } : r)));

  const save = async () => {
    setSaving(true);
    setError(null);
    setErrors([]);
    try {
      setConfig(await ClaimsService.saveBillingConfig(config));
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
      const list = err instanceof ApiError ? err.details?.errors : undefined;
      if (Array.isArray(list)) setErrors(list.filter((e): e is string => typeof e === 'string'));
    } finally {
      setSaving(false);
    }
  };

  const missingRates = config.serviceCodes.filter((r) => r.ratePerUnit === null || r.ratePerUnit === undefined).length;

  return (
    <div className="space-y-4 p-4 text-left" data-testid="billing-config">
      <div>
        <h2 className="text-lg font-medium text-slate-800">Billing codes and rates</h2>
        <p className="text-sm text-slate-500">Codes, modifiers and contracted rates used on every claim line. Charges are rate × units.</p>
      </div>
      {config.isDefault && (
        <Banner variant="info" title="Suggested Texas PDN codes">
          These rows are suggestions (T1000 per 15 minutes, RN TD / LVN TE, UA for vent/trach). Check them against your TMHP and MCO contracts, enter your rates with their source and save.
        </Banner>
      )}
      {missingRates > 0 && (
        <Banner variant="warning">{missingRates} row{missingRates === 1 ? ' has' : 's have'} no rate. Visits using {missingRates === 1 ? 'it' : 'them'} cannot be billed.</Banner>
      )}

      <div className="overflow-x-auto rounded-md border border-slate-200">
        <table className="min-w-full text-sm" aria-label="Service codes">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-2 py-2 text-left">Payer</th>
              <th className="px-2 py-2 text-left">Service</th>
              <th className="px-2 py-2 text-left">Discipline</th>
              <th className="px-2 py-2 text-left">Code</th>
              <th className="px-2 py-2 text-left">Modifiers</th>
              <th className="px-2 py-2 text-left">Unit</th>
              <th className="px-2 py-2 text-left">Min/unit</th>
              <th className="px-2 py-2 text-left">Rate/unit ($)</th>
              <th className="px-2 py-2 text-left">Rate source</th>
              <th className="px-2 py-2 text-left">Effective from</th>
              <th className="px-2 py-2 text-left">PA required</th>
              <th className="px-2 py-2 text-left" title={EVV_UNITS_MATCHED_TIP}>Units matched in EVV</th>
              <th className="px-2 py-2"><span className="sr-only">Remove</span></th>
            </tr>
          </thead>
          <tbody>
            {config.serviceCodes.map((r, i) => (
              <tr key={r.id || i} className="border-t border-slate-100">
                <td className="px-2 py-1"><input aria-label={`Payer, row ${i + 1}`} className={cell} value={r.payerKey} onChange={(e) => setRow(i, { payerKey: e.target.value.toUpperCase() })} /></td>
                <td className="px-2 py-1">
                  <select aria-label={`Service, row ${i + 1}`} className={cell} value={r.service} onChange={(e) => setRow(i, { service: e.target.value as BillingService })}>
                    {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-2 py-1"><input aria-label={`Discipline, row ${i + 1}`} className={`${cell} w-16`} value={r.discipline} onChange={(e) => setRow(i, { discipline: e.target.value.toUpperCase() })} /></td>
                <td className="px-2 py-1"><input aria-label={`Code, row ${i + 1}`} className={`${cell} w-20`} maxLength={5} value={r.hcpcs} onChange={(e) => setRow(i, { hcpcs: e.target.value.toUpperCase() })} /></td>
                <td className="px-2 py-1"><input aria-label={`Modifiers, row ${i + 1}`} className={`${cell} w-24`} value={r.modifiers.join(' ')} onChange={(e) => setRow(i, { modifiers: e.target.value.toUpperCase().split(/[\s,]+/).filter(Boolean) })} /></td>
                <td className="px-2 py-1">
                  <select aria-label={`Unit type, row ${i + 1}`} className={cell} value={r.unitType} onChange={(e) => setRow(i, { unitType: e.target.value as ServiceCodeRow['unitType'], minutesPerUnit: e.target.value === 'visits' ? null : r.minutesPerUnit ?? 15 })}>
                    <option value="units">Time units</option>
                    <option value="visits">Per visit</option>
                  </select>
                </td>
                <td className="px-2 py-1">
                  <input aria-label={`Minutes per unit, row ${i + 1}`} type="number" min={1} className={`${cell} w-16`} disabled={r.unitType === 'visits'} value={r.minutesPerUnit ?? ''} onChange={(e) => setRow(i, { minutesPerUnit: e.target.value === '' ? null : Number(e.target.value) })} />
                </td>
                <td className="px-2 py-1">
                  <input aria-label={`Rate per unit, row ${i + 1}`} type="number" min={0} step="0.01" className={`${cell} w-24`} value={r.ratePerUnit ?? ''} onChange={(e) => setRow(i, { ratePerUnit: e.target.value === '' ? null : Number(e.target.value) })} />
                </td>
                <td className="px-2 py-1"><input aria-label={`Rate source, row ${i + 1}`} className={`${cell} w-40`} placeholder="Fee schedule or contract" value={r.rateSource ?? ''} onChange={(e) => setRow(i, { rateSource: e.target.value || null })} /></td>
                <td className="px-2 py-1"><input aria-label={`Rate effective from, row ${i + 1}`} type="date" className={`${cell} w-36`} value={r.rateEffectiveFrom ?? ''} onChange={(e) => setRow(i, { rateEffectiveFrom: e.target.value || null })} /></td>
                <td className="px-2 py-1 text-center"><input aria-label={`PA required, row ${i + 1}`} type="checkbox" checked={r.requiresAuth} onChange={(e) => setRow(i, { requiresAuth: e.target.checked })} /></td>
                <td className="px-2 py-1 text-center">
                  <input
                    aria-label={`Units matched in EVV, row ${i + 1}`}
                    title={EVV_UNITS_MATCHED_TIP}
                    type="checkbox"
                    checked={isEvvUnitsMatched(r)}
                    onChange={(e) => setRow(i, { evvUnitsMatched: e.target.checked })}
                  />
                </td>
                <td className="px-2 py-1">
                  <button type="button" aria-label={`Remove row ${i + 1}`} onClick={() => { setSaved(false); setConfig({ ...config, serviceCodes: config.serviceCodes.filter((_, k) => k !== i) }); }} className="text-slate-400 hover:text-red-600"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <button type="button" onClick={() => { setSaved(false); setConfig({ ...config, serviceCodes: [...config.serviceCodes, emptyRow()] }); }} className="flex items-center gap-1 text-sm text-slate-700 hover:text-slate-900">
          <Plus size={16} /> Add row
        </button>
        {/* Phase 20E: suggested codes for disciplines billed only where a payer pays them */}
        {(config.suggestions || []).length > 0 && (
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <span>Add a suggested code</span>
            <select
              aria-label="Add a suggested code"
              className={`${cell} w-auto`}
              value=""
              onChange={(e) => {
                const picked = (config.suggestions || [])[Number(e.target.value)];
                if (!picked) return;
                setSaved(false);
                setConfig({
                  ...config,
                  serviceCodes: [...config.serviceCodes, { ...picked, rateSource: null, rateEffectiveFrom: null }],
                  suggestions: (config.suggestions || []).filter((s) => s !== picked),
                });
              }}
            >
              <option value="">Choose…</option>
              {(config.suggestions || []).map((s, i) => (
                <option key={`${s.payerKey}-${s.service}-${s.discipline}-${s.hcpcs}`} value={i}>
                  {s.hcpcs} · {s.service} · {s.discipline} · {s.payerKey}: {s.description}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <section className="space-y-2" aria-labelledby="claim-forms-heading">
        <h3 id="claim-forms-heading" className="text-base font-medium text-slate-800">Claim forms by payer</h3>
        <p className="text-sm text-slate-500">
          Pelora includes the forms confirmed for TMHP and some MCOs. A payer and service with no confirmed form cannot be billed until you add it here,
          with where you confirmed it (provider manual page, or a provider relations call and its date). Lines with modifier UA are used only for PDN
          authorizations approved with UA.
        </p>
        {formRows.length > 0 && (
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <table className="min-w-full text-sm" aria-label="Claim forms">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-2 py-2 text-left">Payer</th>
                  <th className="px-2 py-2 text-left">Service</th>
                  <th className="px-2 py-2 text-left">Form</th>
                  <th className="px-2 py-2 text-left">Type of bill</th>
                  <th className="px-2 py-2 text-left">Revenue code</th>
                  <th className="px-2 py-2 text-left">Lines</th>
                  <th className="px-2 py-2 text-left">Portal</th>
                  <th className="px-2 py-2 text-left">Confirmed where</th>
                  <th className="px-2 py-2"><span className="sr-only">Remove</span></th>
                </tr>
              </thead>
              <tbody>
                {formRows.map((r, i) => (
                  <tr key={`claim-form-${i}`} className="border-t border-slate-100">
                    <td className="px-2 py-1"><input aria-label={`Claim form payer, row ${i + 1}`} className={`${cell} w-48`} value={r.payerKey} onChange={(e) => setFormRow(i, { payerKey: e.target.value.toUpperCase() })} /></td>
                    <td className="px-2 py-1">
                      <select aria-label={`Claim form service, row ${i + 1}`} className={cell} value={r.service} onChange={(e) => setFormRow(i, { service: e.target.value as BillingService | '*' })}>
                        <option value="*">Any</option>
                        {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-1">
                      <select aria-label={`Claim form, row ${i + 1}`} className={cell} value={r.rule.form} onChange={(e) => setFormRow(i, {}, { form: e.target.value as ClaimForm })}>
                        <option value="CMS1500">CMS-1500</option>
                        <option value="UB04">UB-04</option>
                      </select>
                    </td>
                    <td className="px-2 py-1"><input aria-label={`Type of bill, row ${i + 1}`} className={`${cell} w-16`} disabled={r.rule.form !== 'UB04'} value={r.rule.tob ?? ''} placeholder="0321" onChange={(e) => setFormRow(i, {}, { tob: e.target.value })} /></td>
                    <td className="px-2 py-1"><input aria-label={`Revenue code, row ${i + 1}`} className={`${cell} w-16`} disabled={r.rule.form !== 'UB04'} value={r.rule.revenueCode ?? ''} placeholder="0552" onChange={(e) => setFormRow(i, {}, { revenueCode: e.target.value || null })} /></td>
                    <td className="px-2 py-1">
                      <select aria-label={`Line policy, row ${i + 1}`} className={cell} value={r.rule.linePolicy ?? ''} onChange={(e) => setFormRow(i, {}, { linePolicy: (e.target.value || undefined) as ClaimRuleOverride['linePolicy'] })}>
                        <option value="">Default (PDN per day)</option>
                        <option value="PER_DAY">One line per day</option>
                        <option value="PER_VISIT">One line per visit</option>
                      </select>
                    </td>
                    <td className="px-2 py-1">
                      <select aria-label={`Portal, row ${i + 1}`} className={cell} value={r.rule.portal ?? ''} onChange={(e) => setFormRow(i, {}, { portal: (e.target.value || undefined) as ClaimPortal | undefined })}>
                        <option value="">Not set</option>
                        {PORTALS.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-1"><input aria-label={`Confirmed where, row ${i + 1}`} className={`${cell} w-56`} value={r.rule.source} placeholder="e.g. Superior manual p.200, 2026-10" onChange={(e) => setFormRow(i, {}, { source: e.target.value })} /></td>
                    <td className="px-2 py-1">
                      <button type="button" aria-label={`Remove claim form row ${i + 1}`} onClick={() => setFormRows(formRows.filter((_, k) => k !== i))} className="text-slate-400 hover:text-red-600"><Trash2 size={16} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <button type="button" onClick={() => setFormRows([...formRows, emptyClaimFormRow()])} className="flex items-center gap-1 text-sm text-slate-700 hover:text-slate-900">
          <Plus size={16} /> Add claim form
        </button>
      </section>

      <fieldset className="rounded-md border border-slate-200 p-3">
        <legend className="px-1 text-sm font-medium text-slate-700">Days since service (ready-to-bill list)</legend>
        <div className="flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-2">Amber at
            <input type="number" min={1} className={`${cell} w-20`} value={config.agingDays.amber} onChange={(e) => { setSaved(false); setConfig({ ...config, agingDays: { ...config.agingDays, amber: Number(e.target.value) } }); }} />
          </label>
          <label className="flex items-center gap-2">Red at
            <input type="number" min={2} className={`${cell} w-20`} value={config.agingDays.red} onChange={(e) => { setSaved(false); setConfig({ ...config, agingDays: { ...config.agingDays, red: Number(e.target.value) } }); }} />
          </label>
        </div>
        <p className="mt-1 text-xs text-slate-500">Payer filing limits vary; set these below your shortest timely-filing limit.</p>
      </fieldset>

      {error && (
        <div role="alert" className="text-sm text-red-700">
          <p>{error}</p>
          {errors.length > 0 && <ul className="mt-1 list-disc pl-5">{errors.map((e) => <li key={e}>{e}</li>)}</ul>}
        </div>
      )}
      {saved && <p role="status" className="text-sm text-green-700">Saved.</p>}
      <button type="button" onClick={save} disabled={saving} className="rounded-md bg-slate-800 px-4 py-2 text-sm text-white disabled:bg-slate-400">
        {saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
};

export default BillingConfig;
